import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  Link,
  Progress,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { subDays } from "date-fns";
import { AddIcon } from "@chakra-ui/icons";
import { MdKeyboardVoice, MdAssistant } from "react-icons/md";
import { BiMessageSquareDetail } from "react-icons/bi";
import { SiJquery } from "react-icons/si";
import { useNavigate } from "react-router-dom";

import CustomDateRangePicker from "../../utils/CustomDateRange/CustomDateRangePicker";
import { getHostDomain } from "../../utils/utils";
import AuthContext from "../Auth/AuthContext";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import { WrappedModal } from "../WithInstructionModal/WrappedModal";
import CalendlyWidgetModal from "./CalendlyWidgetModal";
import StatCard from "./StatCard";
import useSummary from "./useSummary";
import useActivityTimeseries from "./useActivityTimeseries";
import useTopAssistants from "./useTopAssistants";
import useChannelBreakdown from "./useChannelBreakdown";
import useUserCredits from "./useUserCredits";
import useMonthlyUsages from "./useMonthlyUsages";
import useTopContacts from "./useTopContacts";

const ActivityChart = lazy(() => import("./ActivityChart"));
const TopAssistantsChart = lazy(() => import("./TopAssistantsChart"));
const TopContactsChart = lazy(() => import("./TopContactsChart"));
const ChannelDonut = lazy(() => import("./ChannelDonut"));
const MonthlyTrendChart = lazy(() => import("./MonthlyTrendChart"));

function ChartSpinner() {
  return (
    <Flex justify="center" align="center" h="200px">
      <Spinner size="sm" color="gray.300" />
    </Flex>
  );
}

function PlanHealthCard({ credits }) {
  const { cardBg } = useContext(AgencyContext);
  const alloc = credits?.allocated || {};
  const util = credits?.utilized || {};

  const textUsed = util.queries_count || 0;
  const textTotal = alloc.queries_count || 0;
  const voiceUsedSecs = util.voice_seconds || 0;
  const voiceTotalSecs = alloc.voice_seconds || 0;
  const voiceUsedMin = Math.round(voiceUsedSecs / 60);
  const voiceTotalMin = Math.round(voiceTotalSecs / 60);
  const textPct = textTotal ? Math.min(100, Math.round((textUsed / textTotal) * 100)) : 0;
  const voicePct = voiceTotalMin ? Math.min(100, Math.round((voiceUsedMin / voiceTotalMin) * 100)) : 0;

  return (
    <Card variant="elevated" bg={cardBg} h="100%">
      <CardBody p={4}>
        <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={4}>
          Plan Health
        </Text>
        <Flex direction="column" gap={4}>
          <Box>
            <Flex justify="space-between" mb={1}>
              <Text fontSize="xs" color="gray.600">Text Queries</Text>
              <Text fontSize="xs" color="gray.500">{textUsed.toLocaleString()} / {textTotal.toLocaleString()}</Text>
            </Flex>
            <Tooltip label={`${textPct}% used`} placement="top">
              <Progress
                value={textPct}
                size="sm"
                colorScheme={textPct > 85 ? "red" : textPct > 60 ? "yellow" : "blue"}
                borderRadius="full"
              />
            </Tooltip>
          </Box>
          <Box>
            <Flex justify="space-between" mb={1}>
              <Text fontSize="xs" color="gray.600">Voice Minutes</Text>
              <Text fontSize="xs" color="gray.500">{voiceUsedMin.toLocaleString()} / {voiceTotalMin.toLocaleString()} min</Text>
            </Flex>
            <Tooltip label={`${voicePct}% used`} placement="top">
              <Progress
                value={voicePct}
                size="sm"
                colorScheme={voicePct > 85 ? "red" : voicePct > 60 ? "yellow" : "purple"}
                borderRadius="full"
              />
            </Tooltip>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
}

function formatMinutes(totalMinutes) {
  if (!totalMinutes) return "0 min";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { authUser } = useContext(AuthContext);
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme, myDetails } = agency;
  const isAgencyLoaded = !agency.loading && !myDetails?.agency_id;
  const { state: activePlan } = useContext(SettingContext);

  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  });

  const { summary, status: summaryStatus } = useSummary({ dateRange });
  const { timeseries, status: timeseriesStatus } = useActivityTimeseries({ dateRange });
  const { topAssistants, status: topAssistantsStatus } = useTopAssistants({ dateRange });
  const { channelBreakdown, status: channelStatus } = useChannelBreakdown({ dateRange });
  const { topContacts, status: topContactsStatus } = useTopContacts({ dateRange });
  const { credits } = useUserCredits();
  const { monthlyUsages, status: monthlyStatus } = useMonthlyUsages();

  const { onToggle: onCalendlyToggle, isOpen: isCalendlyOpen } = useDisclosure();
  const toggleCalendly = () => {
    localStorage.setItem("calendly", !isCalendlyOpen);
    onCalendlyToggle();
  };
  useEffect(() => {
    if (!localStorage.getItem("calendly")) onCalendlyToggle();
  }, []);

  const email = myDetails?.agency_id
    ? myDetails?.branding?.agency_mail
    : "support@insighto.ai";

  const firstName =
    authUser?.first_name ||
    authUser?.name?.split(" ")[0] ||
    authUser?.nickname ||
    authUser?.given_name ||
    authUser?.email;

  const hasNoActivity = summary.conversations.current === 0;

  return (
    <>
      <Flex direction="column" gap={4} h="100%" overflowY="auto" pb={4}>

        {/* Top bar */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} data-tour="dashboard.welcome">
          <Flex align="center" gap={2}>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Hi {firstName}! Your AI agents are active.
            </Text>
          </Flex>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
          />
        </Flex>

        {/* Row 1: Stat cards + Plan health */}
        <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4,1fr) 1.5fr" }} gap={4}>
          <StatCard
            title="Conversations"
            value={summaryStatus === "loading" ? "…" : summary.conversations.current.toLocaleString()}
            delta_pct={summaryStatus === "loading" ? null : summary.conversations.delta_pct}
            icon={BiMessageSquareDetail}
            isLoading={summaryStatus === "loading"}
          />
          <StatCard
            title="Queries"
            value={summaryStatus === "loading" ? "…" : summary.queries.current.toLocaleString()}
            delta_pct={summaryStatus === "loading" ? null : summary.queries.delta_pct}
            icon={SiJquery}
            isLoading={summaryStatus === "loading"}
          />
          <StatCard
            title="Voice Interactions"
            value={summaryStatus === "loading" ? "…" : summary.voice_queries.current.toLocaleString()}
            delta_pct={summaryStatus === "loading" ? null : summary.voice_queries.delta_pct}
            icon={MdKeyboardVoice}
            isLoading={summaryStatus === "loading"}
          />
          <StatCard
            title="Avg Conv. Length"
            value={summaryStatus === "loading" ? "…" : `${summary.avg_chat_length.current} msgs`}
            delta_pct={summaryStatus === "loading" ? null : summary.avg_chat_length.delta_pct}
            icon={MdAssistant}
            isLoading={summaryStatus === "loading"}
          />
          <PlanHealthCard credits={credits} />
        </Grid>

        {/* Row 2: Activity chart */}
        <Card variant="elevated">
          <CardBody>
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontWeight="semibold" color={textColor}>Conversation Activity</Text>
              {hasNoActivity && timeseriesStatus !== "loading" && (
                <Button
                  size="xs"
                  colorScheme={buttonColorScheme}
                  leftIcon={<AddIcon />}
                  onClick={() => navigate("/ai-agents/assistants/new")}
                >
                  Create your first AI Agent
                </Button>
              )}
            </Flex>
            {timeseriesStatus === "loading" ? (
              <ChartSpinner />
            ) : hasNoActivity ? (
              <Flex align="center" justify="center" h="240px" direction="column" gap={3}>
                <Text color="gray.400" fontSize="sm">No conversations yet in this period.</Text>
              </Flex>
            ) : (
              <Suspense fallback={<ChartSpinner />}>
                <ActivityChart timeseries={timeseries} />
              </Suspense>
            )}
          </CardBody>
        </Card>

        {/* Row 3: Top Assistants + Top Contacts + Channel Breakdown */}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4}>
          <Card variant="elevated">
            <CardBody>
              <Text fontWeight="semibold" color={textColor} mb={4}>Top Assistants</Text>
              {topAssistantsStatus === "loading" ? (
                <ChartSpinner />
              ) : (
                <Suspense fallback={<ChartSpinner />}>
                  <TopAssistantsChart topAssistants={topAssistants} />
                </Suspense>
              )}
            </CardBody>
          </Card>
          <Card variant="elevated">
            <CardBody>
              <Text fontWeight="semibold" color={textColor} mb={4}>Top Contacts</Text>
              {topContactsStatus === "loading" ? (
                <ChartSpinner />
              ) : (
                <Suspense fallback={<ChartSpinner />}>
                  <TopContactsChart topContacts={topContacts} />
                </Suspense>
              )}
            </CardBody>
          </Card>
          <Card variant="elevated">
            <CardBody>
              <Text fontWeight="semibold" color={textColor} mb={4}>Traffic by Channel</Text>
              {channelStatus === "loading" ? (
                <ChartSpinner />
              ) : (
                <Suspense fallback={<ChartSpinner />}>
                  <ChannelDonut channelBreakdown={channelBreakdown} />
                </Suspense>
              )}
            </CardBody>
          </Card>
        </Grid>

        {/* Row 4: Monthly trend */}
        <Card variant="elevated">
          <CardBody>
            <Text fontWeight="semibold" color={textColor} mb={4}>Monthly Usage (Last 12 Months)</Text>
            {monthlyStatus === "loading" ? (
              <ChartSpinner />
            ) : (
              <Suspense fallback={<ChartSpinner />}>
                <MonthlyTrendChart monthlyUsages={monthlyUsages} />
              </Suspense>
            )}
          </CardBody>
        </Card>

        {/* Footer */}
        {email && (
          <Flex justify="flex-end">
            <Button colorScheme={buttonColorScheme} size="sm">
              <Link target="_blank" href={`mailto:${email}`} style={{ textDecoration: "none" }}>
                Queries? We're here — {email}
              </Link>
            </Button>
          </Flex>
        )}
      </Flex>

      {isCalendlyOpen && isAgencyLoaded && activePlan?.plan_lookup_key !== "202402_appsumo_4" && (
        <CalendlyWidgetModal isOpen={isCalendlyOpen} onClose={toggleCalendly} />
      )}
    </>
  );
};

export default WrappedModal(Dashboard);
